import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/apiService';
import { showToast } from '../components/Toast';
import { parseErrorMessage } from '../utils/formErrors';
import { DSAlert, DSBadge, DSButton, DSIcon, DSInput, DSPageHeader, DSStatTile, DSTableShell, DSToggle } from '../design-system/primitives';
import DeveloperBottomNav from '../components/DeveloperBottomNav';
import './BoardGameLibrary.css';

// The board game library is kept in the club's inventory spreadsheet, not here. This page
// shows the app's copy of it and when it was last refreshed.

const STYLE_LABELS = { competitive: 'Competitive', 'co-op': 'Co-op', asymmetric: 'Asymmetric', team: 'Team' };

function formatWhen(value) {
  if (!value) return 'never';
  return new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function players(game) {
  if (game.min_players && game.max_players) {
    return game.min_players === game.max_players ? `${game.min_players}` : `${game.min_players} to ${game.max_players}`;
  }
  return game.min_players ? `${game.min_players}+` : '';
}

export default function BoardGameLibrary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [query, setQuery] = useState('');
  const [privateOnly, setPrivateOnly] = useState(false);
  const [showRetired, setShowRetired] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await apiService.get('/board-games');
      setData(response.data);
    } catch (err) {
      showToast(parseErrorMessage(err, 'Could not load the board game library.'), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data: summary } = await apiService.post('/board-games/sync');
      const parts = [`${summary.added} added`, `${summary.updated} updated`, `${summary.retired} retired`];
      if (summary.bggFetched) parts.push(`${summary.bggFetched} updated from BoardGameGeek`);
      showToast(`Library synced: ${parts.join(', ')}.`, 'success');
      await load();
    } catch (err) {
      showToast(parseErrorMessage(err, 'The sync did not finish.'), 'error', 8000);
      await load();
    } finally {
      setSyncing(false);
    }
  };

  const games = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.games || []).filter((g) => (showRetired ? true : g.is_active)
      && (!privateOnly || g.private_collection)
      && (!q || g.name.toLowerCase().includes(q)));
  }, [data, query, privateOnly, showRetired]);

  if (loading) {
    return <div className="page bn-page-padding"><DSPageHeader title="Board Game Library" /><p>Loading...</p><DeveloperBottomNav /></div>;
  }

  const counts = data?.counts || {};
  const last = data?.lastImport;

  return (
    <div className="page board-game-library bn-page-padding">
      <DSPageHeader
        title="Board Game Library"
        description="Kept in the club's inventory spreadsheet. The app copies it every night, and the website shows this list."
        actions={(
          <DSButton
            appearance="solid"
            tone="primary"
            leadingIcon={<DSIcon name="refresh" size="sm" />}
            onClick={syncNow}
            disabled={syncing || !data?.configured?.sheet}
          >
            {syncing ? 'Syncing...' : 'Sync now'}
          </DSButton>
        )}
      />

      {!data?.configured?.sheet && (
        <DSAlert variant="warning">
          The inventory sheet's CSV link isn't set yet (BOARD_GAMES_SHEET_CSV_URL on the server), so the library can't refresh.
        </DSAlert>
      )}
      {data?.configured?.sheet && !data?.configured?.bgg && (
        <DSAlert variant="info" appearance="subtle">
          Cover art and max players come from BoardGameGeek, which needs an API token (BGG_API_TOKEN on the server). Until it's set, games show the spreadsheet's details only.
        </DSAlert>
      )}

      <div className="board-game-library__stats">
        <DSStatTile label="Games in the library" value={counts.active ?? 0} />
        <DSStatTile
          label="From a private collection"
          value={counts.private_collection ?? 0}
          onClick={() => setPrivateOnly((v) => !v)}
          subtext={privateOnly ? 'Showing only these' : 'Select to list them'}
        />
        <DSStatTile label="Retired" value={counts.retired ?? 0} subtext="No longer in the sheet" />
        <DSStatTile
          label="Last sync"
          value={last ? (last.success ? 'OK' : 'Failed') : 'Never'}
          tone={last && !last.success ? 'danger' : 'neutral'}
          subtext={last ? formatWhen(last.finished_at || last.started_at) : ''}
        />
      </div>

      {last && !last.success && last.error && (
        <DSAlert variant="danger">The last sync failed: {last.error}</DSAlert>
      )}

      <section className="card">
        <div className="board-game-library__filters">
          <DSInput label="Search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Game name" />
          <DSToggle id="bgl-private" label="Private collection only" checked={privateOnly} onChange={(e) => setPrivateOnly(e.target.checked)} />
          <DSToggle id="bgl-retired" label="Include retired" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
        </div>
        <p className="board-game-library__muted">{games.length} shown</p>
        <div className="board-game-library__table">
          <DSTableShell
            columns={['Game', 'Players', 'Minutes', 'Rules', 'Style', 'Collection', '']}
            rows={games.map((g) => [
              <span className="board-game-library__name" key="n">
                {g.thumbnail_url ? <img src={g.thumbnail_url} alt="" /> : null}
                <span>
                  {g.name}
                  {g.copies > 1 ? <span className="board-game-library__muted"> ({g.copies} copies)</span> : null}
                  {!g.is_active ? <span className="board-game-library__muted"> (retired)</span> : null}
                </span>
              </span>,
              players(g),
              g.play_minutes || '',
              g.difficulty || '',
              STYLE_LABELS[g.play_style] || g.play_style || '',
              g.private_collection ? <DSBadge variant="info" appearance="subtle" size="sm" key="p">Private</DSBadge> : 'Club',
              g.bgg_url ? <a key="l" href={g.bgg_url} target="_blank" rel="noreferrer">BGG</a> : '',
            ])}
          />
        </div>
      </section>

      <DeveloperBottomNav />
    </div>
  );
}
